import { QueryClient, MutationCache } from '@tanstack/react-query';
import { toast } from '@mlm/ui';
import { apiErrorMessage } from './api';

/**
 * Typed mutation `meta`. A mutation opts into a success toast by declaring
 * `meta: { successMessage: 'Saved.' }`; `errorMessage` overrides the default
 * server-derived error toast for that mutation.
 */
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      successMessage?: string;
      errorMessage?: string;
    };
  }
}

/**
 * Single shared QueryClient instance. Lives outside main.tsx so the api
 * layer can call `queryClient.clear()` on logout / auth loss — otherwise
 * stale data from the previous user leaks into the next session and the
 * user has to Ctrl+F5 to see fresh data.
 *
 * The MutationCache gives every save/mutation feedback for free: any failure
 * toasts the server's error message (no silent failures anywhere), and a
 * mutation shows a success toast when it sets `meta.successMessage`.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      // By default React Query PAUSES everything while the browser says it is
      // offline, so nothing would ever reach the answers kept on this device.
      networkMode: 'always',
    },
    mutations: { networkMode: 'always' },
  },
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      toast.error(
        mutation.meta?.errorMessage ??
          apiErrorMessage(error, 'Something went wrong. Please try again.'),
      );
    },
    onSuccess: (_data, _vars, _ctx, mutation) => {
      if (mutation.meta?.successMessage) toast.success(mutation.meta.successMessage);
    },
  }),
});
